import { useEffect, useRef } from 'react';
import { onboardingService } from '../services/onboardingService';
import { RelanceOnboardingStatus } from '../types/onboarding';
import { useOnboardingStore } from '../stores/onboardingStore';
import { useTenantStore } from '../stores/tenantStore';
import { useAcombaBridgeAccessReady } from './useAcombaBridgeAccessReady';
import { useAcombaBridgeStore } from '../stores/acombaBridgeStore';
import { useIsAcomba } from './useIsAcomba';

/**
 * Fetches the company's terms-of-use decision once the session is ready and sets
 * `onboardingStore.onboardingRequired`, which RootLayout gates the full-screen walkthrough on.
 *
 * It runs while either half is outstanding: this user has never been welcomed, or the company has
 * not decided and this user is one we can route (see `Onboarding` for how far each gets). The welcome
 * is per user, so a newcomer still gets it long after the company accepted; the decision screens are
 * the company's and are never shown again once it has answered.
 *
 * Mirrors useResumeSync's shape (a standalone effect keyed on `ready` and the company, mounted once from
 * RootLayout) rather than being threaded through every one of useAuthGuard's session-establishment
 * branches, which already number more than a dozen.
 */
export function useOnboardingGate(ready: boolean): void {
    const setOnboardingRequired = useOnboardingStore(s => s.setOnboardingRequired);
    const setOnboardingResolved = useOnboardingStore(s => s.setOnboardingResolved);
    const setStatus = useOnboardingStore(s => s.setStatus);
    const setIsAdministrator = useOnboardingStore(s => s.setIsAdministrator);
    const setHasSeenWelcome = useOnboardingStore(s => s.setHasSeenWelcome);
    const ciu = useTenantStore(s => s.ciu);
    const tenantCode = useTenantStore(s => s.tenantCode);
    const tenantSwitchedAt = useTenantStore(s => s.tenantSwitchedAt);
    const fetchedForSwitchRef = useRef(tenantSwitchedAt);
    // The desktop flag only reaches the backend once the bridge has pushed it; re-read when it lands.
    const bridgeAccessReady = useAcombaBridgeAccessReady();
    const isAcomba = useIsAcomba();
    const bridgeAccess = useAcombaBridgeStore(s => s.functionalityAccess);
    const companyMatch = useAcombaBridgeStore(s => s.companyMatchStatus);
    // Whether a host actually reported the flag, rather than it merely reading false — unknowable
    // before the bridge answers, on a pre-1.2.0 bridge, and on a company mismatch.
    const administratorFlagReported = !isAcomba
        || (bridgeAccess?.isSupervisor !== undefined && companyMatch !== 'mismatch');

    useEffect(() => {
        if (!ready || !ciu) return;

        if (fetchedForSwitchRef.current !== tenantSwitchedAt) {
            fetchedForSwitchRef.current = tenantSwitchedAt;
            setOnboardingResolved(false);
            setOnboardingRequired(false);
            setStatus(null);
        }

        let cancelled = false;
        void (async () => {
            try {
                const { status, isAdministrator, hasSeenWelcome } = await onboardingService.getStatus();
                if (cancelled) return;
                setStatus(status);
                setIsAdministrator(isAdministrator);
                setHasSeenWelcome(hasSeenWelcome);

                // An unresolved flag must never strand someone on "ask your administrator" when they
                // may be the administrator.
                // Neither half is gated on relance access — the app's own no-section-access message
                // comes after the company has answered, not instead of the walkthrough.
                const undecided = status === RelanceOnboardingStatus.NotStarted;
                const decisionOutstanding = undecided && (isAdministrator || administratorFlagReported);
                setOnboardingRequired(!hasSeenWelcome || decisionOutstanding);
            } catch (e) {
                // Fail open, or one transient failure pins the overlay for the session. The backend
                // still refuses every sync until the company has accepted.
                if (!cancelled) setOnboardingRequired(false);
                console.error('Failed to fetch onboarding status:', e);
            } finally {
                // Set even on failure, or RootLayout spins forever.
                if (!cancelled) setOnboardingResolved(true);
            }
        })();
        return () => { cancelled = true; };
    }, [ready, ciu, tenantCode, tenantSwitchedAt, bridgeAccessReady, administratorFlagReported,
        setOnboardingRequired, setOnboardingResolved, setStatus, setIsAdministrator, setHasSeenWelcome]);
}
